import React, { useState, useEffect, useMemo } from "react";
import { useLocation } from "wouter";
import { Box, Typography, Link, Divider, Alert } from "@mui/material";
import {
  ArrowBack,
  Gavel,
  ContactSupport,
  Security,
  KeyboardArrowUp,
  Assignment,
  AccountBox,
  Policy,
  Warning,
  Cancel,
  Update,
} from "@mui/icons-material";

import logo from "../../assets/nwt.png";
import {
  PrivacyCard,
  PrivacyContainer,
  ScrollTopSlot,
  SectionCard,
  TableOfContents,
} from "./styles";
import { Section } from "./types";
import { Fab } from "../../components/Fab";

export const TermsOfServicePage: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>("");
  const [showScrollTop, setShowScrollTop] = useState(false);

  const [, navigate] = useLocation();

  const lastUpdated = "June 21, 2026";
  const version = "2.2";

  useEffect(() => {
    let frame = 0;

    const handleScroll = () => {
      if (frame) {
        return;
      }
      frame = globalThis.window.requestAnimationFrame(() => {
        setShowScrollTop(window.scrollY > 300);
        frame = 0;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (frame) {
        globalThis.window.cancelAnimationFrame(frame);
      }
    };
  }, []);

  useEffect(() => {
    const sectionEls = document.querySelectorAll("[data-section]");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }
          const id = (entry.target as HTMLElement).dataset.section;
          if (id) {
            setActiveSection(id);
          }
        });
      },
      { rootMargin: "-100px 0px -66% 0px" },
    );

    sectionEls.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const goBack = () => {
    if (globalThis.window.history.length > 1) {
      globalThis.window.history.back();
      return;
    }
    navigate("/");
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.querySelector(`[data-section="${sectionId}"]`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const sections: Section[] = useMemo(
    () => [
      {
        id: "acceptance",
        title: "Acceptance of Terms",
        icon: <Gavel sx={{ color: "text.secondary" }} />,
        content: (
          <Box>
            <Typography variant="body1" gutterBottom>
              By accessing and using Net Worth Tracker ("the Service"), you
              accept and agree to be bound by the terms and provision of this
              agreement. If you do not agree to abide by the above, please do
              not use this service.
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Important:</strong> These Terms of Service ("Terms")
                constitute a legally binding agreement between you and Net Worth
                Tracker.
              </Typography>
            </Alert>
            <Typography variant="body1">
              These Terms govern your use of our financial management platform
              and constitute a legally binding agreement between you and Net
              Worth Tracker ("Company", "we", "us", or "our").
            </Typography>
          </Box>
        ),
      },
      {
        id: "description",
        title: "Service Description",
        icon: <Assignment sx={{ color: "text.secondary" }} />,
        content: (
          <Box>
            <Typography variant="body1" gutterBottom>
              Net Worth Tracker is a personal financial management platform that
              allows users to:
            </Typography>
            <SectionCard>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: "text.primary" }}
              >
                Core Features
              </Typography>
              <Box component="ul" sx={{ pl: 3, m: 0 }}>
                <Typography component="li" variant="body2">
                  Track income, expenses, and financial goals
                </Typography>
                <Typography component="li" variant="body2">
                  Monitor investment portfolios and net worth
                </Typography>
                <Typography component="li" variant="body2">
                  Generate financial reports and insights
                </Typography>
              </Box>
            </SectionCard>
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Important Disclaimer:</strong> We do not provide
                financial advice, investment recommendations, or professional
                financial services. Our platform is for informational and
                organizational purposes only.
              </Typography>
            </Alert>
          </Box>
        ),
      },
      {
        id: "accounts",
        title: "User Accounts",
        icon: <AccountBox sx={{ color: "text.secondary" }} />,
        content: (
          <Box>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "text.primary" }}
            >
              Account Registration
            </Typography>
            <Typography variant="body1" gutterBottom>
              To use certain features of the Service, you must register for an
              account and provide accurate, current, and complete information.
              You must also be at least 18 years old to create an account.
            </Typography>
            <Typography variant="body1" gutterBottom>
              You are responsible for safeguarding your account credentials and
              for all activities that occur under your account.
            </Typography>

            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "text.primary", mt: 3 }}
            >
              Account Security
            </Typography>
            <Typography variant="body1" gutterBottom>
              You agree to immediately notify us of any unauthorized use of your
              account or any other breach of security. We will not be liable for
              any losses caused by any unauthorized use of your account.
            </Typography>

            <SectionCard>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: "text.primary" }}
              >
                Password Requirements
              </Typography>
              <Typography variant="body2" gutterBottom>
                Your password must meet the following requirements:
              </Typography>
              <Box component="ul" sx={{ pl: 3, m: 0 }}>
                <Typography component="li" variant="body2">
                  Minimum 8 characters in length
                </Typography>
                <Typography component="li" variant="body2">
                  At least one uppercase letter
                </Typography>
                <Typography component="li" variant="body2">
                  At least one lowercase letter
                </Typography>
                <Typography component="li" variant="body2">
                  At least one number
                </Typography>
                <Typography component="li" variant="body2">
                  At least one special character
                </Typography>
              </Box>
            </SectionCard>
          </Box>
        ),
      },
      {
        id: "conduct",
        title: "User Conduct",
        icon: <Policy sx={{ color: "text.secondary" }} />,
        content: (
          <Box>
            <Typography variant="body1" gutterBottom>
              You agree not to use the Service to:
            </Typography>

            <Box sx={{ display: "grid", gap: 2 }}>
              <SectionCard>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ color: "text.primary" }}
                >
                  Prohibited Activities
                </Typography>
                <Box component="ul" sx={{ pl: 3, m: 0 }}>
                  <Typography component="li" variant="body2">
                    Violate any applicable laws or regulations
                  </Typography>
                  <Typography component="li" variant="body2">
                    Transmit any harmful, offensive, or inappropriate content
                  </Typography>
                  <Typography component="li" variant="body2">
                    Attempt to gain unauthorized access to our systems
                  </Typography>
                  <Typography component="li" variant="body2">
                    Interfere with or disrupt the Service or servers
                  </Typography>
                  <Typography component="li" variant="body2">
                    Use automated systems to access the Service without
                    permission
                  </Typography>
                </Box>
              </SectionCard>
            </Box>
          </Box>
        ),
      },
      {
        id: "privacy",
        title: "Privacy & Data Protection",
        icon: <Security sx={{ color: "text.secondary" }} />,
        content: (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Your privacy is important to us. Please review our{" "}
                <Link
                  href="/privacy"
                  sx={{ color: "var(--color-primary)" }}
                  onClick={() => navigate("/privacy")}
                >
                  Privacy Policy
                </Link>{" "}
                to understand in detail how we collect, use, store, and protect
                your information.
              </Typography>
            </Alert>

            <Typography variant="body1" gutterBottom>
              We implement industry-standard security measures to protect your
              personal and financial data, as described in our Privacy Policy.
              However, no method of transmission over the Internet is 100%
              secure, and we cannot guarantee absolute security.
            </Typography>

            <SectionCard>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: "text.primary" }}
              >
                Data Ownership
              </Typography>
              <Typography variant="body2">
                You retain ownership of all data you input into the Service. We
                do not claim ownership of your financial information or personal
                data.
              </Typography>
            </SectionCard>

            <SectionCard>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: "text.primary" }}
              >
                International Data Transfers
              </Typography>
              <Typography variant="body2">
                Your information may be processed and stored on servers located
                outside your country of residence, as described in our Privacy
                Policy. We take steps to ensure adequate safeguards are in place
                when transferring data internationally.
              </Typography>
            </SectionCard>
          </Box>
        ),
      },
      {
        id: "intellectual",
        title: "Intellectual Property",
        icon: <Security sx={{ color: "text.secondary" }} />,
        content: (
          <Box>
            <Typography variant="body1">
              The Service and its original content, features, and functionality
              are and will remain the exclusive property of Net Worth Tracker
              and its licensors. The Service is protected by copyright,
              trademark, and other laws.
            </Typography>

            <SectionCard>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: "text.primary" }}
              >
                Your Rights
              </Typography>
              <Typography variant="body2">
                While we retain ownership of the platform, you maintain full
                ownership of any data, content, or information you provide
                through our Service.
              </Typography>
            </SectionCard>
          </Box>
        ),
      },
      {
        id: "disclaimers",
        title: "Disclaimers",
        icon: <Warning sx={{ color: "text.secondary" }} />,
        content: (
          <Box>
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Important Financial Disclaimer:</strong> Net Worth
                Tracker is not a financial advisor, broker, or investment
                company. We do not provide investment advice or recommendations.
              </Typography>
            </Alert>

            <Typography variant="body1">
              The information provided by the Service is for informational
              purposes only and should not be considered as financial,
              investment, tax, or legal advice. You should consult with
              qualified professionals before making any financial decisions.
            </Typography>

            <SectionCard>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: "text.primary" }}
              >
                No Warranty
              </Typography>
              <Typography variant="body2">
                The Service is provided on an "as is" and "as available" basis.
                We make no representations or warranties of any kind, express or
                implied, regarding the use or the results of this Service.
              </Typography>
            </SectionCard>
          </Box>
        ),
      },
      {
        id: "limitation",
        title: "Limitation of Liability",
        icon: <Warning sx={{ color: "text.secondary" }} />,
        content: (
          <Box>
            <Typography variant="body1" gutterBottom>
              In no event shall Net Worth Tracker, its directors, employees, or
              agents be liable for any indirect, incidental, special,
              consequential, or punitive damages, including without limitation,
              loss of profits, data, use, goodwill, or other intangible losses.
            </Typography>

            <SectionCard>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: "text.primary" }}
              >
                Maximum Liability
              </Typography>
              <Typography variant="body2">
                Our total liability to you for all claims arising from or
                relating to the Service shall not exceed the greater of (a) the
                total fees you paid us for the Service during the twelve months
                preceding the claim, or (b) 100 EUR.
              </Typography>
            </SectionCard>
          </Box>
        ),
      },
      {
        id: "termination",
        title: "Termination",
        icon: <Cancel sx={{ color: "text.secondary" }} />,
        content: (
          <Box>
            <Typography variant="body1" gutterBottom>
              We may terminate or suspend your account immediately, without
              prior notice or liability, for any reason whatsoever, including
              without limitation if you breach the Terms.
            </Typography>

            <Typography variant="body1" gutterBottom>
              Upon termination, your right to use the Service will cease
              immediately. If you wish to terminate your account, you may simply
              discontinue using the Service.
            </Typography>

            <SectionCard>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: "text.primary" }}
              >
                Effect of Termination
              </Typography>
              <Typography variant="body2">
                Upon termination, we will make reasonable efforts to delete your
                personal data, except where retention is required by law or for
                legitimate business purposes as outlined in our Privacy Policy.
              </Typography>
            </SectionCard>
          </Box>
        ),
      },
      {
        id: "changes",
        title: "Changes to Terms",
        icon: <Update sx={{ color: "text.secondary" }} />,
        content: (
          <Box>
            <Typography variant="body1" gutterBottom>
              We reserve the right, at our sole discretion, to modify or replace
              these Terms at any time. If a revision is material, we will try to
              provide at least 30 days notice prior to any new terms taking
              effect.
            </Typography>

            <SectionCard>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: "text.primary" }}
              >
                Notification Process
              </Typography>
              <Box component="ul" sx={{ pl: 3, m: 0 }}>
                <Typography component="li" variant="body2">
                  Email notifications for material changes
                </Typography>
                <Typography component="li" variant="body2">
                  In-app notifications about updates
                </Typography>
                <Typography component="li" variant="body2">
                  30-day notice period for significant changes
                </Typography>
              </Box>
            </SectionCard>
          </Box>
        ),
      },
      {
        id: "contact",
        title: "Contact Information",
        icon: <ContactSupport sx={{ color: "text.secondary" }} />,
        content: (
          <Box>
            <Typography variant="body1" gutterBottom>
              If you have any questions about these Terms of Service, please
              contact us:
            </Typography>

            <SectionCard>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: "text.primary" }}
              >
                Legal Department
              </Typography>
              <Box component="ul" sx={{ pl: 3, m: 0 }}>
                <Typography component="li" variant="body2">
                  <strong>Email:</strong>{" "}
                  <Link
                    href="mailto:my.ntw.app@gmail.com"
                    sx={{ color: "var(--color-primary)" }}
                  >
                    my.ntw.app@gmail.com
                  </Link>
                </Typography>
              </Box>
            </SectionCard>
          </Box>
        ),
      },
    ],
    [navigate],
  );

  const tableOfContents = sections.map((section) => ({
    id: section.id,
    title: section.title,
    icon: section.icon,
  }));

  return (
    <PrivacyContainer>
      <PrivacyCard elevation={0}>
        {/* Header */}
        <Box component="header" sx={{ mb: 5 }}>
          <Link
            component="button"
            onClick={goBack}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              mb: 4,
              color: "var(--color-primary)",
              fontWeight: "medium",
              textDecoration: "none",
            }}
          >
            <ArrowBack fontSize="small" />
            Back
          </Link>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
            <Box
              component="img"
              src={logo}
              alt=""
              sx={{ height: 28, width: "auto", display: "block" }}
            />
            <Typography
              variant="overline"
              sx={{ color: "text.secondary", letterSpacing: "0.08em" }}
            >
              Net Worth Tracker
            </Typography>
          </Box>

          <Typography
            variant="h3"
            component="h1"
            sx={{ fontWeight: "bold", color: "text.primary", mb: 1 }}
          >
            Terms of Service
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Version {version} · Effective {lastUpdated}
          </Typography>
        </Box>

        {/* Important Notice */}
        <Alert severity="info" sx={{ mb: 4 }}>
          <Typography variant="body2">
            <strong>Important:</strong> Please read these terms carefully before
            using Net Worth Tracker. By creating an account or using our
            service, you agree to be bound by these terms.
          </Typography>
        </Alert>

        {/* Table of Contents */}
        <TableOfContents>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontWeight: "bold", color: "text.primary" }}
          >
            Table of Contents
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
              gap: 1,
            }}
          >
            {tableOfContents.map((item, index) => (
              <Link
                key={item.id}
                component="button"
                variant="body2"
                onClick={() => scrollToSection(item.id)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  p: 1,
                  borderRadius: 1,
                  textAlign: "left",
                  border: "none",
                  textDecoration: "none",
                  background:
                    activeSection === item.id
                      ? "color-mix(in srgb, var(--color-primary) 8%, transparent)"
                      : "transparent",
                  color:
                    activeSection === item.id
                      ? "var(--color-primary)"
                      : "text.primary",
                  "&:hover": {
                    background:
                      "color-mix(in srgb, var(--color-primary) 5%, transparent)",
                  },
                }}
              >
                {item.icon}
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: activeSection === item.id ? "medium" : "normal",
                  }}
                >
                  {index + 1}. {item.title}
                </Typography>
              </Link>
            ))}
          </Box>
        </TableOfContents>

        {/* Content Sections */}
        <Box>
          {sections.map((section, index) => (
            <Box
              key={section.id}
              id={section.id}
              data-section={section.id}
              sx={{ mb: 5, scrollMarginTop: "24px" }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
              >
                {section.icon}
                <Typography
                  variant="h5"
                  sx={{ fontWeight: "bold", color: "text.primary" }}
                >
                  {index + 1}. {section.title}
                </Typography>
              </Box>
              {section.content}
              {index < sections.length - 1 && <Divider sx={{ mt: 4 }} />}
            </Box>
          ))}
        </Box>

        {/* Footer */}
        <Divider sx={{ my: 4 }} />
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            These terms are effective as of {lastUpdated}
          </Typography>
        </Box>
      </PrivacyCard>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <ScrollTopSlot>
          <Fab aria-label="Back to top" onClick={scrollToTop}>
            <KeyboardArrowUp />
          </Fab>
        </ScrollTopSlot>
      )}
    </PrivacyContainer>
  );
};
